import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { apolloService } from '@/services/api';
import { logger, createDebouncedCloudStorage } from '@/utils';
import type { Visitor, AsyncState } from '@/types';

interface ParsedVisitorImport {
  domain: string;
  company?: string;
  visits?: number;
  lastVisit?: string;
  pagesViewed?: string[];
  timeOnSite?: string;
  icpScore?: number;
  intent?: 'High' | 'Medium-High' | 'Medium' | 'Low';
}

// ============================================================================
// Visitor Periods (multi-import support)
// ============================================================================

export interface VisitorPeriod {
  id: string;
  label: string;
  importedAt: string;
  visitors: Visitor[];
}

const VISITOR_PERIODS_KEY = 'mih_visitor_periods';

const debouncedPeriodsStorage = createDebouncedCloudStorage<VisitorPeriod[]>(
  VISITOR_PERIODS_KEY, 'visitor-periods', 3000
);

function loadVisitorPeriods(): VisitorPeriod[] {
  try {
    const stored = localStorage.getItem(VISITOR_PERIODS_KEY);
    if (stored) return JSON.parse(stored) as VisitorPeriod[];
  } catch {}
  return [];
}

interface UseVisitorsConfig {
  useMockData: boolean;
  initialData?: Visitor[];
}

export function useVisitors({ initialData = [] }: UseVisitorsConfig) {
  const [visitorPeriods, setVisitorPeriods] = useState<VisitorPeriod[]>(loadVisitorPeriods);
  const [activePeriodId, setActivePeriodId] = useState<string | null>(
    () => {
      const periods = loadVisitorPeriods();
      return periods.length > 0 ? periods[periods.length - 1]!.id : null;
    }
  );

  // Active period's visitors (or initialData/demo data if no periods)
  const activePeriod = visitorPeriods.find(p => p.id === activePeriodId) ?? null;
  const activeVisitors = activePeriod?.visitors ?? (visitorPeriods.length === 0 ? initialData : []);

  // Previous period for "new" badge comparison
  const previousPeriod = useMemo(() => {
    if (!activePeriodId) return null;
    const idx = visitorPeriods.findIndex(p => p.id === activePeriodId);
    return idx > 0 ? visitorPeriods[idx - 1] ?? null : null;
  }, [visitorPeriods, activePeriodId]);

  const previousDomains = useMemo(() => {
    if (!previousPeriod) return new Set<string>();
    return new Set(previousPeriod.visitors.map(v => v.enrichment?.domain || v.company.toLowerCase()));
  }, [previousPeriod]);

  const [visitors, setVisitors] = useState<AsyncState<Visitor[]>>({
    data: activeVisitors,
    loading: false,
    error: null,
  });

  // Sync visitors state when active period changes
  useEffect(() => {
    setVisitors({ data: activeVisitors, loading: false, error: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePeriodId, visitorPeriods]);

  // Persist periods to localStorage + DynamoDB
  useEffect(() => {
    if (visitorPeriods.length > 0) {
      try { localStorage.setItem(VISITOR_PERIODS_KEY, JSON.stringify(visitorPeriods)); } catch {}
      debouncedPeriodsStorage.write(visitorPeriods);
    }
  }, [visitorPeriods]);

  // Load from DynamoDB on mount if localStorage is empty
  useEffect(() => {
    if (visitorPeriods.length === 0) {
      debouncedPeriodsStorage.loadFromCloud().then(cloudData => {
        if (cloudData && cloudData.length > 0) {
          setVisitorPeriods(cloudData);
          setActivePeriodId(cloudData[cloudData.length - 1]!.id);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedVisitors, setSelectedVisitors] = useState<number[]>([]);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [importingVisitors, setImportingVisitors] = useState(false);

  const fetched = useRef(false);

  const fetchVisitors = useCallback(async () => {
    setVisitors((prev) => ({ ...prev, loading: prev.data.length === 0, error: null }));

    try {
      const data = await apolloService.getVisitors();
      setVisitors({ data, loading: false, error: null });
    } catch (error) {
      setVisitors((prev) => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  }, []);

  const toggleVisitorSelection = useCallback((id: number) => {
    setSelectedVisitors((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  }, []);

  const importVisitors = useCallback(
    async (parsedVisitors: ParsedVisitorImport[]) => {
      setImportingVisitors(true);

      try {
        logger.log(`[Hub] Importing ${parsedVisitors.length} visitors from CSV...`);

        const newVisitors: Visitor[] = [];
        const seenDomains = new Set<string>();

        for (let i = 0; i < parsedVisitors.length; i++) {
          const parsed = parsedVisitors[i];
          if (!parsed) continue;

          const domain = parsed.domain;

          // Skip duplicates within this import batch
          if (seenDomains.has(domain)) continue;
          seenDomains.add(domain);

          const domainParts = domain.split('.');
          const companyFromDomain = domainParts[0]
            ? domainParts[0].charAt(0).toUpperCase() + domainParts[0].slice(1)
            : 'Unknown';

          const icpScore = parsed.icpScore ?? (50 + Math.floor(Math.random() * 30));
          const intent = parsed.intent ??
            (icpScore >= 75 ? 'High' as const :
             icpScore >= 55 ? 'Medium-High' as const :
             icpScore >= 35 ? 'Medium' as const : 'Low' as const);

          const newVisitor: Visitor = {
            id: i + 1,
            company: parsed.company ?? companyFromDomain,
            industry: 'Unknown',
            size: 'Unknown',
            location: 'Unknown',
            visits: parsed.visits ?? 1,
            lastVisit: parsed.lastVisit ?? new Date().toISOString().split('T')[0] ?? '',
            pagesViewed: parsed.pagesViewed ?? ['/'],
            timeOnSite: parsed.timeOnSite ?? '0m 0s',
            icpScore,
            intent,
            suggestedContacts: [],
            enrichment: { domain },
          };

          newVisitors.push(newVisitor);
        }

        if (newVisitors.length > 0) {
          const today = new Date();
          const periodId = crypto.randomUUID();
          const label = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

          const newPeriod: VisitorPeriod = {
            id: periodId,
            label: `Import ${label}`,
            importedAt: today.toISOString(),
            visitors: newVisitors,
          };

          setVisitorPeriods(prev => [...prev, newPeriod]);
          setActivePeriodId(periodId);
          logger.log(`[Hub] Successfully imported ${newVisitors.length} visitors as new period "${newPeriod.label}"`);
        }

        return { imported: newVisitors.length, skipped: parsedVisitors.length - newVisitors.length, enrichmentFailures: 0 };
      } finally {
        setImportingVisitors(false);
      }
    },
    []
  );

  const deleteVisitorPeriod = useCallback((periodId: string) => {
    setVisitorPeriods(prev => {
      const next = prev.filter(p => p.id !== periodId);
      if (activePeriodId === periodId) {
        setActivePeriodId(next.length > 0 ? next[next.length - 1]!.id : null);
      }
      return next;
    });
  }, [activePeriodId]);

  return {
    // Data
    visitors: visitors.data,
    visitorsLoading: visitors.loading,
    visitorsError: visitors.error,

    // Periods
    visitorPeriods,
    activePeriodId,
    setActivePeriodId,
    deleteVisitorPeriod,
    previousDomains,

    // Selection
    selectedVisitors,
    selectedVisitor,
    setSelectedVisitor,

    // Actions
    fetchVisitors,
    toggleVisitorSelection,
    importVisitors,
    importingVisitors,

    // Internal (for orchestrator)
    setVisitors,
    fetched,
    setSelectedVisitors,
  };
}

export type UseVisitorsReturn = ReturnType<typeof useVisitors>;
